import type { MedicationRequest } from '@medplum/fhirtypes';

import { Grid, Stack, Text } from '@eluve/components';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import type { FhirView } from '../../core/view-contract';
import { resourceDisplayName } from '../accessors';
import { formatFhirDate } from '../observation/format';

/**
 * The exported MedicationRequest component. Medication name comes from the
 * inline CodeableConcept when present; a fetched medicationReference target
 * arrives pre-rendered on `slots.medicationReference` (MedicationView inline).
 */
export const MedicationRequestView: FhirView<MedicationRequest> = (props) => {
  if (props.ctx.inList) return <MedicationRequestRowView {...props} />;
  return <MedicationRequestReadView {...props} />;
};

function MedicationName({
  data,
  slots,
}: {
  data: MedicationRequest;
  slots: { medicationReference?: React.ReactNode };
}) {
  if (data.medicationCodeableConcept?.text) {
    return (
      <Text inline size="sm" weight="regular" className="truncate">
        {data.medicationCodeableConcept.text}
      </Text>
    );
  }
  if (slots.medicationReference) return <>{slots.medicationReference}</>;
  return (
    <Text inline size="sm" weight="regular" className="truncate">
      {resourceDisplayName(data)}
    </Text>
  );
}

function dosageText(data: MedicationRequest): string | undefined {
  return data.dosageInstruction?.[0]?.text;
}

/** Med-list row: name, dosage instruction, status, authored date. */
const MedicationRequestRowView: FhirView<MedicationRequest> = ({
  data,
  slots,
}) => {
  const dosage = dosageText(data);
  const date = formatFhirDate(data.authoredOn);
  return (
    <Grid
      data-view="medication-request-row"
      align="center"
      className="grid-cols-[minmax(0,1fr)_auto] gap-x-4 py-2"
    >
      <div className="min-w-0">
        <Stack
          direction="horizontal"
          gap={2}
          align="center"
          className="min-w-0"
        >
          <MedicationName data={data} slots={slots} />
          <FhirStatusTag status={data.status} />
        </Stack>
        {dosage && (
          <Text size="xs" color="tertiary" className="truncate">
            {dosage}
          </Text>
        )}
      </div>
      {date && (
        <Text inline size="xs" color="tertiary">
          {date}
        </Text>
      )}
    </Grid>
  );
};

/** Standalone card. */
const MedicationRequestReadView: FhirView<MedicationRequest> = ({
  data,
  slots,
  ctx,
}) => {
  const dosage = dosageText(data);
  const date = formatFhirDate(data.authoredOn);
  const requester = data.requester?.display;
  return (
    <div
      data-view="medication-request-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <MedicationName data={data} slots={slots} />
        <FhirStatusTag status={data.status} />
      </Stack>
      {dosage && (
        <Text size="sm" weight="regular" className="mt-1">
          {dosage}
        </Text>
      )}
      <Stack
        direction="horizontal"
        wrap
        align="stretch"
        className="mt-1 gap-x-4"
      >
        {date && (
          <Text inline size="xs" color="tertiary">
            Prescribed {date}
          </Text>
        )}
        {requester && (
          <Text inline size="xs" color="tertiary">
            {requester}
          </Text>
        )}
      </Stack>
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};
